import React from 'react';
import _get from 'lodash/get';
import classNames from 'classnames';
import { transparentize } from 'polished';
import _isEmpty from 'lodash/isEmpty';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import SettingItem from './SettingItem';
import { Widget, PersonListsByDatePersonList } from 'pages/Homepage/typings';
import { MAX_CHAR_COUNT_NAME } from 'pages/Homepage/helpers/consts';
import {
  UPDATE_FREQUENCIES,
  DISPLAY_YEAR_OPTIONS,
  DISPLAY_FIRST_YEAR_OPTIONS,
  DISPLAY_RECURRENCE_OPTIONS,
} from './consts';

interface Props {
  values: Widget;
  listIndex: number;
  errors: any;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  onReorderList: (currentIndex: number, targetIndex: number) => void;
  onDeleteList: (index: number) => void;
}

function PersonsListByDateForm({
  values,
  listIndex,
  errors,
  setFieldValue,
  onReorderList,
  onDeleteList,
}: Props) {
  const classes = useStyles();
  const entityTypes = useEntityTypes();
  const userConfig = _get(entityTypes, PERSON_ENTITY_TYPE_KEY);
  const { _fields } = userConfig;
  const dateFields = Object.values(_fields)
    .filter(f => f.type === 'date')
    .map(f => ({ displayName: f.displayName, value: f.objectKey }));

  const personLists: Array<PersonListsByDatePersonList> = _get(
    values,
    ['settings', 'personLists'],
    [],
  );
  const isMultiList = personLists.length > 1;
  const disableReordering = !isMultiList;
  const baseListKey = `personLists[${listIndex}]`;
  const personList = personLists[listIndex];
  const {
    name,
    displayYear,
    fieldObjectKey,
    includeYearly,
    updateFrequency,
    includeInFirstYear,
    _targetListIndex,
  } = personList;
  const disableIncludeFirstYear = values.settings && !includeYearly;

  const setSelectBooleanValue = (formKey: string, formValue: string) => {
    const boolValue = formValue === 'true';

    setFieldValue(
      formKey,
      // (Note - Dylan) convert boolean string to boolean value (MenuItem cannot take boolean as value)
      boolValue,
    );
  };

  return (
    <Card className={classes.root}>
      <div className={classes.settingContainer}>
        <div>
          <Typography variant="h6">
            List #{_targetListIndex + 1}
            {name ? ':' : ''} {name}
          </Typography>
        </div>
        <div>
          <Button onClick={() => onDeleteList(listIndex)} className={classes.deleteButton}>
            Delete List
          </Button>
        </div>
      </div>

      <Divider variant="fullWidth" className={classes.divider} />

      <div className={classes.nameSetting}>
        <Typography variant="h6" className={classNames(!isMultiList && classes.disabled)}>
          List Name
        </Typography>
        <Typography variant="subtitle2" className={classNames(!isMultiList && classes.disabled)}>
          If you have more than one list, this list name will appear as the title of the tab/section
          in the widget. If you only have one list, use the widget title above to describe this
          list.
        </Typography>
        <Tooltip
          arrow
          placement="top"
          title={
            !isMultiList ? 'You may only change your list name if you have more than one list' : ''
          }
        >
          <div>
            <TextField
              id="list-name"
              name="list-name"
              disabled={!isMultiList}
              fullWidth
              error={isMultiList && !name}
              value={name}
              onChange={e =>
                setFieldValue(`settings.personLists[${listIndex}].name`, e.target.value)
              }
              InputLabelProps={{
                classes: {
                  root: classes.inputLabel,
                  focused: classes.cssFocused,
                },
              }}
              inputProps={{
                maxLength: MAX_CHAR_COUNT_NAME,
                'aria-label': 'list name',
              }}
              helperText={
                errors[`personLists[${listIndex}].name`] && errors[`personLists[${listIndex}].name`]
              }
            />
          </div>
        </Tooltip>
      </div>

      <SettingItem
        title="Tab Order"
        subTitle="Which order should this list appear in your widget?"
        value={_targetListIndex}
        options={personLists.map((_, index: number) => ({
          displayName: `List #${index + 1}`,
          value: index,
        }))}
        disabled={disableReordering}
        tooltipContent={'You may only change your tab order if you have more than one list'}
        onChange={e => onReorderList(_targetListIndex, e.target.value as number)}
      />
      <SettingItem
        title="Update Frequency"
        subTitle="How often should we change who is featured in this list? We recommend choosing an option
            that will have matching people more days than not. (Ex: If you are a smaller
            organization, you may not want to choose “Daily”)."
        value={updateFrequency}
        options={UPDATE_FREQUENCIES}
        onChange={e =>
          setFieldValue(`settings.personLists[${listIndex}].updateFrequency`, e.target.value)
        }
      />
      <SettingItem
        title="Source Date Attribute"
        subTitle="Choose a date attribute that we should use to find people in this list. (Ex: for
            anniversaries and new hires, we would base the list on a “Hire Date” attribute)."
        value={fieldObjectKey}
        options={dateFields}
        errorMessage={_get(errors, [`${baseListKey}.fieldObjectKey`])}
        onChange={e =>
          setFieldValue(`settings.personLists[${listIndex}].fieldObjectKey`, e.target.value)
        }
      />
      <SettingItem
        title="Should the year be displayed with the date?"
        subTitle="Should we display the year of the milstone on the widget? Or just the month and day?"
        value={displayYear}
        options={DISPLAY_YEAR_OPTIONS}
        onChange={e =>
          setSelectBooleanValue(
            `settings.personLists[${listIndex}].displayYear`,
            e.target.value as string,
          )
        }
      />
      <SettingItem
        title="Should people be featured on a recurring basis?"
        subTitle="Do you want people to be featured on the corresponding month and day every year (ex:
            Anniversary, Birthday)? Or only once near the exact date (e.x: New Hire)?"
        value={includeYearly}
        options={DISPLAY_RECURRENCE_OPTIONS}
        onChange={e =>
          setSelectBooleanValue(
            `settings.personLists[${listIndex}].includeYearly`,
            e.target.value as string,
          )
        }
      />
      <SettingItem
        title="Should people be featured the first year?"
        subTitle="Do you want people to be displayed every single year? Or should we skip them the first
            year? (Ex: You only want to start with anniversaries after a year has passed)."
        value={disableIncludeFirstYear ? true : includeInFirstYear}
        options={DISPLAY_FIRST_YEAR_OPTIONS}
        disabled={disableIncludeFirstYear}
        tooltipContent="This option is only available when showing recurring events"
        onChange={e =>
          setSelectBooleanValue(
            `settings.personLists[${listIndex}].includeInFirstYear`,
            e.target.value as string,
          )
        }
      />
    </Card>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  inputLabel: {
    '&$cssFocused': {
      color: theme.palette.secondary.main,
    },
  },
  cssFocused: {},
  settingContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  nameSetting: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  divider: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  disabled: {
    color: transparentize(0.62, theme.palette.common.black),
  },
  deleteButton: {
    color: theme.colors.error,
  },
}));

export default PersonsListByDateForm;
